import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  UserCheck, ChevronLeft, ChevronRight, AlertTriangle,
  Users, Building2, CalendarDays, List,
} from 'lucide-react';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import PillToggle from '../UI/PillToggle';
import { CountBadge } from '../UI/CountBadge';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import FormSelect from '../UI/FormSelect';
import { getClassificationAvatarHex } from '../../utils/classificationConfig';
import { getCurrentYearInSingaporeTimezone, getCurrentMonthKeySingapore } from '../../utils/dateUtils';
import { getDepartmentIcon, getDepartmentColor } from '../../config/departmentConfig';
import {
  CalendarGrid,
  CalendarLegend,
  ClockPatternSummary,
  WeeklyHoursChart,
  LeaveBalanceCards,
  ComplianceAlerts,
  AttendanceExceptions,
  WorkArrangementSummary,
} from '../shared/AttendanceDetail';
import DailyDetailView from '../shared/AttendanceDetail/DailyDetailView';
import MonthNavigator from '../UI/MonthNavigator';

function getMemberInitial(fullName = '') {
  return fullName.trim().charAt(0).toUpperCase() || '?';
}

const CLASSIFICATION_FILTERS = [
  { key: 'all',    label: 'All' },
  { key: 'staff',  label: 'Staff' },
  { key: 'worker', label: 'Worker' },
];

const VIEW_MODES = [
  { id: 'calendar', icon: CalendarDays, label: 'Calendar view' },
  { id: 'detail',   icon: List,         label: 'Daily detail view' },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(!e.matches);
    handler(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Detail cards (shared between mobile and desktop layouts) ──

function DetailContent({
  data, isLoading, error, month, onMonthChange, isWorker, showMonthNav = true,
  isHOD = false,
  viewMode, onViewModeChange, expandedDays, onExpandedChange,
  expandableDateKeys, allExpanded, allCollapsed, expandAll, collapseAll,
}) {
  const showDetailToggle = isHOD;
  return (
    <>
      {showMonthNav && (
        <div className="flex items-center justify-end">
          <MonthNavigator month={month} onChange={onMonthChange} theme="employee" maxMonth={getCurrentMonthKeySingapore()} />
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
          <p className="font-medium">Failed to load individual detail</p>
          <p className="text-sm mt-1">{error.response?.data?.detail || error.message}</p>
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{data.full_name}</h3>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <ClassificationBadge classification={data.classification} />
                  {data.department && (
                    <span className="text-xs text-gray-500">{data.department}</span>
                  )}
                  {data.schedule_label && (
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2.5 py-0.5 rounded-full ring-1 ring-gray-400/20">
                      {data.schedule_label}
                    </span>
                  )}
                </div>
              </div>
              <WorkArrangementSummary summary={data.work_arrangement_summary} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <h4 className="text-sm font-semibold text-gray-700 min-w-0 truncate">
                {showDetailToggle && viewMode === 'detail'
                  ? 'Daily Attendance Breakdown'
                  : 'Monthly Attendance Calendar'}
              </h4>
              {showDetailToggle && (
                <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                  {viewMode === 'detail' && expandableDateKeys.length > 0 && (
                    <PillToggle
                      value={allExpanded ? 'expand' : allCollapsed ? 'collapse' : ''}
                      onChange={(val) => val === 'expand' ? expandAll() : collapseAll()}
                      options={[
                        { id: 'expand', label: 'Expand all' },
                        { id: 'collapse', label: 'Collapse all' },
                      ]}
                      variant="text"
                      theme="employee"
                      layout="sectionHeader"
                      ariaLabel="Expand or collapse all days"
                      className="flex-1 sm:flex-initial min-w-0"
                    />
                  )}
                  <PillToggle
                    value={viewMode}
                    onChange={onViewModeChange}
                    options={VIEW_MODES.map((opt) => ({ id: opt.id, label: opt.label, icon: opt.icon }))}
                    variant="icon"
                    theme="employee"
                    ariaLabel="Calendar or detail view"
                  />
                </div>
              )}
            </div>
            {showDetailToggle && viewMode === 'detail' ? (
              <DailyDetailView
                dailyStatus={data.daily_status}
                isWorker={isWorker}
                expandedDays={expandedDays}
                onExpandedChange={onExpandedChange}
              />
            ) : (
              <>
                <CalendarGrid dailyStatus={data.daily_status} />
                <CalendarLegend dailyStatus={data.daily_status} />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Clock Pattern (Present Days)</h4>
              <ClockPatternSummary pattern={data.clock_pattern_summary} />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <WeeklyHoursChart weeklyHours={data.weekly_hours_summary} isWorker={isWorker} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Leave Balances ({getCurrentYearInSingaporeTimezone()})
            </h4>
            <LeaveBalanceCards balances={(data.leave_balances ?? []).filter((lb) => {
              const isCompassionate = (lb.leave_type ?? '').toLowerCase().includes('compassionate');
              return !isCompassionate || lb.used > 0;
            })} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Compliance Alerts</h4>
              <ComplianceAlerts alerts={data.compliance_alerts} />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Attendance Exceptions (This Month)
              </h4>
              <AttendanceExceptions exceptions={data.attendance_exceptions} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Main component ──

export default function IndividualDetail({
  data,
  isLoading,
  error,
  selectedStaffId,
  teamMembers = [],
  onSelectStaff,
  month,
  onMonthChange,
  isHOD = false,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [expandedDays, setExpandedDays] = useState(new Set());
  const isMobile = useIsMobile();

  useEffect(() => { setExpandedDays(new Set()); }, [selectedStaffId, month]);

  const expandableDateKeys = useMemo(() => {
    if (!data?.daily_status) return [];
    return data.daily_status
      .filter(d => d.clock_events?.length > 0)
      .map(d => d.date);
  }, [data?.daily_status]);

  const allExpanded = expandableDateKeys.length > 0 && expandableDateKeys.every(d => expandedDays.has(d));
  const allCollapsed = expandedDays.size === 0;
  const expandAll = useCallback(() => setExpandedDays(new Set(expandableDateKeys)), [expandableDateKeys]);
  const collapseAll = useCallback(() => setExpandedDays(new Set()), []);

  const filteredAndGrouped = useMemo(() => {
    let list = [...teamMembers];

    if (classFilter !== 'all') {
      list = list.filter(
        (m) => (m.classification || '').toLowerCase() === classFilter,
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((m) => {
        const name = (m.full_name || '').toLowerCase();
        const sid = String(m.staff_id || '').toLowerCase();
        return name.includes(q) || sid.includes(q);
      });
    }

    if (!isHOD) {
      return { _flat: list.sort((a, b) => a.full_name.localeCompare(b.full_name)) };
    }

    const grouped = {};
    list.forEach((m) => {
      const group = m.department_name || 'Unassigned';
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(m);
    });
    Object.values(grouped).forEach((arr) =>
      arr.sort((a, b) => a.full_name.localeCompare(b.full_name)),
    );
    return grouped;
  }, [teamMembers, classFilter, searchTerm, isHOD]);

  const totalFiltered = useMemo(
    () => Object.values(filteredAndGrouped).reduce((sum, arr) => sum + arr.length, 0),
    [filteredAndGrouped],
  );

  const isWorker = data?.classification === 'worker';
  const isFlat = '_flat' in filteredAndGrouped;

  // ── Mobile layout: select dropdown + stacked detail ──

  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 font-medium whitespace-nowrap">Viewing:</label>
            <FormSelect
              value={selectedStaffId || ''}
              onChange={(val) => onSelectStaff(val)}
              options={[
                ...(selectedStaffId ? [] : [{ value: '', label: 'Select a team member\u2026' }]),
                ...teamMembers.map((m) => ({ value: m.staff_id, label: m.full_name })),
              ]}
              theme="employee"
              className="text-sm flex-1 min-w-0"
            />
          </div>
          <div className="flex justify-center">
            <MonthNavigator month={month} onChange={onMonthChange} theme="employee" maxMonth={getCurrentMonthKeySingapore()} />
          </div>
        </div>

        {!selectedStaffId ? (
          <div className="text-center py-12 text-gray-400">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <UserCheck className="w-7 h-7 text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-500">Select a team member above</p>
          </div>
        ) : (
          <DetailContent
            data={data}
            isLoading={isLoading}
            error={error}
            month={month}
            onMonthChange={onMonthChange}
            isWorker={isWorker}
            showMonthNav={false}
            isHOD={isHOD}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            expandedDays={expandedDays}
            onExpandedChange={setExpandedDays}
            expandableDateKeys={expandableDateKeys}
            allExpanded={allExpanded}
            allCollapsed={allCollapsed}
            expandAll={expandAll}
            collapseAll={collapseAll}
          />
        )}
      </div>
    );
  }

  // ── Desktop layout: split panel (sidebar + detail) ──

  const renderMemberButton = (m) => {
    const isSelected = String(selectedStaffId) === String(m.staff_id);
    const classification = (m.classification || '').toLowerCase();
    return (
      <button
        key={m.staff_id}
        onClick={() => onSelectStaff(m.staff_id)}
        className={`w-full text-left px-3 py-2.5 transition-all duration-150 border-l-4 ${
          isSelected
            ? 'bg-blue-50 border-blue-800'
            : 'border-transparent hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
            style={{ backgroundColor: getClassificationAvatarHex(classification) }}
          >
            <span className="text-sm font-semibold text-white leading-none">
              {getMemberInitial(m.full_name)}
            </span>
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
              {m.full_name}
            </p>
            <p className="text-xs text-gray-400 truncate">{m.staff_id}</p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="flex gap-4" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
      {/* ── LEFT PANEL: Team Member List ── */}
      <div
        className={`${leftPanelCollapsed ? 'w-12' : 'w-72'} bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col transition-all duration-200 flex-shrink-0`}
      >
        {leftPanelCollapsed ? (
          <div className="flex flex-col items-center h-full">
            <button
              onClick={() => setLeftPanelCollapsed(false)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 mx-auto mt-3 mb-2 flex-shrink-0"
              title="Expand panel"
              aria-label="Expand team member panel"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="flex-1 overflow-y-auto w-full flex flex-col items-center gap-1 pb-2">
              {Object.values(filteredAndGrouped).flat().map((m) => {
                const isSelected = String(selectedStaffId) === String(m.staff_id);
                const classification = (m.classification || '').toLowerCase();
                return (
                  <button
                    key={m.staff_id}
                    onClick={() => onSelectStaff(m.staff_id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white transition-all duration-150 ${
                      isSelected ? 'ring-2 ring-blue-600 ring-offset-1' : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
                    }`}
                    style={{ backgroundColor: getClassificationAvatarHex(classification) }}
                    title={m.full_name}
                    aria-label={m.full_name}
                  >
                    {getMemberInitial(m.full_name)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
              <div className="flex items-center gap-2">
                <CountBadge count={totalFiltered} theme="employee" size="md" />
                <button
                  onClick={() => setLeftPanelCollapsed(true)}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                  title="Collapse panel"
                  aria-label="Collapse team member panel"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="px-3 pt-3 pb-2">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search team..."
                theme="employee"
              />
            </div>

            <div className="px-3 pb-2">
              <PillToggle
                value={classFilter}
                onChange={setClassFilter}
                options={CLASSIFICATION_FILTERS.map(f => ({ id: f.key, label: f.label }))}
                variant="text"
                theme="employee"
                fullWidth
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {teamMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">No team members</p>
                </div>
              ) : totalFiltered === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">No matches found</p>
                </div>
              ) : isFlat ? (
                filteredAndGrouped._flat.map(renderMemberButton)
              ) : (
                Object.entries(filteredAndGrouped)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([group, members]) => (
                    <div key={group}>
                      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                        <div className="flex items-center gap-1.5">
                          {(() => { const DI = getDepartmentIcon(group); return <DI className={`w-3 h-3 ${getDepartmentColor(group).text}`} />; })()}
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
                            {group}
                          </span>
                          <CountBadge count={members.length} theme="employee" />
                        </div>
                      </div>
                      {members.map(renderMemberButton)}
                    </div>
                  ))
              )}
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT PANEL: Detail ── */}
      <div className="flex-1 overflow-y-auto transition-all duration-200 min-w-0">
        {!selectedStaffId ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <UserCheck className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-500">Select a team member to view details</p>
            <p className="text-xs text-gray-400 mt-1">Use the search bar or browse the list</p>
          </div>
        ) : (
          <div className="space-y-4">
            <DetailContent
              data={data}
              isLoading={isLoading}
              error={error}
              month={month}
              onMonthChange={onMonthChange}
              isWorker={isWorker}
              isHOD={isHOD}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              expandedDays={expandedDays}
              onExpandedChange={setExpandedDays}
              expandableDateKeys={expandableDateKeys}
              allExpanded={allExpanded}
              allCollapsed={allCollapsed}
              expandAll={expandAll}
              collapseAll={collapseAll}
            />
          </div>
        )}
      </div>
    </div>
  );
}
